import { ReactComponent as DetailsIcon } from "assets/icons/details.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import { getDropdownValue } from "helpers/DropdownHelper";
import {
    DEFAULT_PAGE_SIZE_OPTION,
    handlePageClick,
    handlePageSizeChange,
} from "helpers/PaginationHelper";
import {
    Button,
    CustomTable,
    NotificationPriority,
    NotificationPriorityColorTranslation,
    NotificationPriorityTranslation,
    NotificationSeverity,
    NotificationSeverityTranslation,
    OptionType,
    Pagination,
    TableColumn,
    TableType,
} from "modules/common";
import { SentNotificationDetails, useGetSentNotifications } from "modules/communication";
import { SentNotificationsFilters } from "modules/communication/components/SentNotificationsFilters";
import { useEffect, useMemo, useState } from "react";
import { Row, SortingRule } from "react-table";
import { NotificationView } from "services/communication";

interface Props {
    onReturn: () => void;
}

export const SentNotifications = ({ onReturn }: Props) => {
    // filters
    const [priorityFilter, setPriorityFilter] = useState<NotificationPriority[]>([]);
    const [severityFilter, setSeverityFilter] = useState<NotificationSeverity[]>([]);
    const [professionalFieldFilter, setProfessionalFieldFilter] = useState<string[]>([]);
    const [searchFilter, setSearchFilter] = useState<string>("");
    const [dateFilter, setDateFilter] = useState<Date | null>(null);

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    // page size
    const [pageSize, setPageSize] = useState<OptionType>(DEFAULT_PAGE_SIZE_OPTION);
    const [currentPage, setCurrentPage] = useState(1);

    // sorting rule
    const [sortBy, setSortBy] = useState<SortingRule<object>>();

    // query
    const { data, isFetching: isLoading } = useGetSentNotifications({
        priority: priorityFilter,
        severity: severityFilter,
        professionalField: professionalFieldFilter,
        search: searchFilter,
        date: dateFilter,
        sortField: sortBy?.id ?? "scheduledAt",
        descending: sortBy?.desc ?? true,
        page: currentPage,
        pageSize: parseInt(pageSize.value),
    });

    // selected notification details
    const [selectedNotification, setSelectedNotification] = useState<NotificationView | null>();

    const tableColumns = useMemo(() => {
        const columns: TableColumn<NotificationView>[] = [
            {
                Header: "NASLOV PORUKE",
                disableSortBy: true,
                width: 3,
                Cell: ({ row }: { row: Row<NotificationView> }) => (
                    <span>{row.original.subject}</span>
                ),
            },
            {
                Header: "PRIORITET",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: { row: Row<NotificationView> }) => (
                    <div className="notification-priority">
                        <div className="priority-marker">
                            <div
                                style={{
                                    backgroundColor: getComputedStyle(
                                        document.documentElement
                                    ).getPropertyValue(
                                        getDropdownValue(
                                            row.original.priority,
                                            NotificationPriorityColorTranslation
                                        ).label
                                    ),
                                }}
                            ></div>
                        </div>
                        <span style={{ whiteSpace: "nowrap" }}>
                            {
                                getDropdownValue(
                                    row.original.priority,
                                    NotificationPriorityTranslation
                                ).label
                            }
                        </span>
                    </div>
                ),
            },
            {
                Header: "DATUM SLANJA",
                width: 1,
                accessor: "scheduledAt",
                Cell: ({ row }: { row: Row<NotificationView> }) => (
                    <span>{getDisplayDateFormat(row.original.scheduledAt)}</span>
                ),
            },
            {
                Header: "VRSTA PORUKE",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: { row: Row<NotificationView> }) => (
                    <span>
                        {
                            getDropdownValue(row.original.severity, NotificationSeverityTranslation)
                                .label
                        }
                    </span>
                ),
            },
            {
                Header: "PRIVITAK",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: { row: Row<NotificationView> }) => (
                    <span>{Object.keys(row.original.attachments).length !== 0 ? "Da" : "Ne"}</span>
                ),
            },
            {
                Header: "ISPORUČENO",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: { row: Row<NotificationView> }) => (
                    <span>
                        {`${row.original.deliveryStats} od ${row.original.total} = ${
                            row.original.total !== 0
                                ? Math.round(
                                      (row.original.deliveryStats / row.original.total) * 100
                                  )
                                : 0
                        }%`}
                    </span>
                ),
            },
            {
                Header: "PROČITANO",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: { row: Row<NotificationView> }) => (
                    <span>
                        {`${row.original.readStats} od ${row.original.total} = ${
                            row.original.total !== 0
                                ? Math.round((row.original.readStats / row.original.total) * 100)
                                : 0
                        }%`}
                    </span>
                ),
            },
            {
                Header: "VIŠE",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: { row: Row<NotificationView> }) => (
                    <Button
                        onClick={() => setSelectedNotification(row.original)}
                        isIcon
                        size="medium"
                    >
                        <DetailsIcon />
                    </Button>
                ),
            },
        ];

        return columns;
    }, []);

    useEffect(() => {
        if (!data?.sentNotificationsCount) return;

        const lastPage = Math.ceil(data?.sentNotificationsCount / parseInt(pageSize.value));
        if (lastPage < currentPage) setCurrentPage(lastPage);
    }, [data?.sentNotificationsCount]);

    return (
        <div className="f-c g-24 f-1">
            {selectedNotification && (
                <SentNotificationDetails
                    notification={selectedNotification}
                    onReturn={() => setSelectedNotification(null)}
                />
            )}

            {!selectedNotification && (
                <>
                    <SentNotificationsFilters
                        priorityFilter={priorityFilter}
                        setPriorityFilter={setPriorityFilter}
                        severityFilter={severityFilter}
                        setSeverityFilter={setSeverityFilter}
                        professionalFieldFilter={professionalFieldFilter}
                        setProfessionalFieldFilter={setProfessionalFieldFilter}
                        setCurrentPage={setCurrentPage}
                        searchFilter={searchFilter}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        setSearchFilter={setSearchFilter}
                        dateFilter={dateFilter}
                        setDateFilter={setDateFilter}
                        onReturn={onReturn}
                    />

                    <CustomTable
                        data={data?.sentNotificationsList ?? []}
                        columns={tableColumns}
                        setSortBy={setSortBy}
                        flex
                        loading={isLoading}
                        tableType={TableType.SentNotificationsTable}
                    />

                    {!isLoading && data && data.sentNotificationsCount !== 0 && (
                        <Pagination
                            currentPage={currentPage}
                            numberOfPages={Math.ceil(
                                data.sentNotificationsCount / parseInt(pageSize.value)
                            )}
                            pageNeighbours={1}
                            pageSize={pageSize}
                            onPageSizeChange={v =>
                                handlePageSizeChange(v, setPageSize, setCurrentPage)
                            }
                            handlePageClick={e => handlePageClick(e, setCurrentPage)}
                            handlePageSelect={e => setCurrentPage(e)}
                            next={() => setCurrentPage(p => p + 1)}
                            prev={() => setCurrentPage(p => p - 1)}
                            first={() => setCurrentPage(1)}
                            last={() =>
                                setCurrentPage(
                                    Math.ceil(
                                        data.sentNotificationsCount / parseInt(pageSize.value)
                                    )
                                )
                            }
                        />
                    )}
                </>
            )}
        </div>
    );
};
